import { motion, useReducedMotion } from "motion/react"
import type { ReactNode } from "react"
import { useEffect, useMemo, useState } from "react"

import { GlobalStage, GlobalView, useGlobalStore, useGlobalView } from "@/store"
import { getFontSize } from "@/utils/adaptFont"

import styles from "./index.module.scss"

const getJellyEntrance = (
    shouldReduceMotion: boolean,
    delay: number,
    drop: number,
) => {
    if (shouldReduceMotion) {
        return {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            transition: {
                duration: 0.16,
                delay,
            },
        }
    }

    return {
        initial: {
            opacity: 0,
            y: -drop,
            scaleX: 0.94,
            scaleY: 1.18,
        },
        animate: {
            opacity: [0, 1, 1, 1, 1],
            y: [-drop, 6, -6, 1, 0],
            scaleX: [0.94, 1.16, 0.97, 1.02, 1],
            scaleY: [1.18, 0.82, 1.04, 0.995, 1],
        },
        transition: {
            duration: 0.64,
            delay,
            times: [0, 0.38, 0.62, 0.82, 1],
        },
    }
}

const REGION_KEYS = [
    "sargon",
    "sami",
    "leithanien",
    "ursus",
    "kazdel",
    "rhodes",
] as const

interface RegionProps {
    active: boolean
    animateOnEnter: boolean
    shouldReduceMotion: boolean
}

type RegionKey = (typeof REGION_KEYS)[number]

type RegionUnlocks = Record<RegionKey, boolean>

interface AnimatedPartProps {
    animateOnEnter: boolean
    children?: ReactNode
    className: string
    delay: number
    drop: number
    shouldReduceMotion: boolean
}

interface RegionDefinition {
    className: string
    idleClassName: string
    key: RegionKey
    parts: readonly RegionPartDefinition[]
    threshold: number
}

interface PatrolSpriteProps {
    spriteClassName: string
    wrapperClassName: string
}

interface RegionPartDefinition {
    children?: ReactNode
    className: string
    delay: number
    drop: number
    key: string
}

interface RegionRenderProps extends RegionProps {
    definition: RegionDefinition
}

const getEntranceProps = (
    animateOnEnter: boolean,
    shouldReduceMotion: boolean,
    delay: number,
    drop: number,
) => (animateOnEnter ? getJellyEntrance(shouldReduceMotion, delay, drop) : {})

const createRegionUnlocks = (value: boolean): RegionUnlocks =>
    REGION_KEYS.reduce((accumulator, key) => {
        accumulator[key] = value
        return accumulator
    }, {} as RegionUnlocks)

const AnimatedPart = ({
    animateOnEnter,
    children,
    className,
    delay,
    drop,
    shouldReduceMotion,
}: AnimatedPartProps) => (
    <motion.div
        className={className}
        {...getEntranceProps(animateOnEnter, shouldReduceMotion, delay, drop)}
    >
        {children}
    </motion.div>
)

const PatrolSprite = ({
    spriteClassName,
    wrapperClassName,
}: PatrolSpriteProps) => (
    <div className={wrapperClassName}>
        <div className={spriteClassName} />
    </div>
)

const Region = ({
    active,
    animateOnEnter,
    definition,
    shouldReduceMotion,
}: RegionRenderProps) => {
    if (!active) {
        return null
    }

    return (
        <div className={definition.className}>
            <div className={definition.idleClassName}>
                {definition.parts.map((part) => (
                    <AnimatedPart
                        key={`${definition.key}-${part.key}`}
                        animateOnEnter={animateOnEnter}
                        className={part.className}
                        delay={part.delay}
                        drop={part.drop}
                        shouldReduceMotion={shouldReduceMotion}
                    >
                        {part.children}
                    </AnimatedPart>
                ))}
            </div>
        </div>
    )
}

const REGIONS = [
    {
        className: styles.sargon,
        idleClassName: styles.sargonIdle,
        key: "sargon",
        parts: [
            {
                children: <div className={styles.plant} />,
                className: styles.block,
                delay: 0.02,
                drop: 26,
                key: "plant-left",
            },
            {
                children: (
                    <>
                        <div className={styles.building} />
                        <div className={styles.plant} />
                    </>
                ),
                className: styles.block,
                delay: 0.12,
                drop: 34,
                key: "building",
            },
            {
                children: (
                    <PatrolSprite
                        spriteClassName={styles.npc}
                        wrapperClassName={styles.npcPatrol}
                    />
                ),
                className: styles.block,
                delay: 0.2,
                drop: 20,
                key: "npc",
            },
            {
                className: styles.mark,
                delay: 0.28,
                drop: 18,
                key: "mark",
            },
        ],
        threshold: 0.16,
    },
    {
        className: styles.sami,
        idleClassName: styles.samiIdle,
        key: "sami",
        parts: [
            {
                children: (
                    <PatrolSprite
                        spriteClassName={styles.npc}
                        wrapperClassName={styles.npcPatrol}
                    />
                ),
                className: styles.block,
                delay: 0.02,
                drop: 22,
                key: "npc",
            },
            {
                children: (
                    <>
                        <div className={styles.tree} />
                        <div className={styles.tree} />
                        <div className={styles.building} />
                    </>
                ),
                className: styles.block,
                delay: 0.12,
                drop: 36,
                key: "building",
            },
            {
                className: styles.mark,
                delay: 0.22,
                drop: 18,
                key: "mark",
            },
        ],
        threshold: 0.33,
    },
    {
        className: styles.leithanien,
        idleClassName: styles.leithanienIdle,
        key: "leithanien",
        parts: [
            {
                children: (
                    <>
                        <div className={styles.building} />
                        <PatrolSprite
                            spriteClassName={styles.npc}
                            wrapperClassName={styles.npcPatrol}
                        />
                    </>
                ),
                className: styles.block,
                delay: 0.02,
                drop: 32,
                key: "building",
            },
            {
                className: styles.mark,
                delay: 0.12,
                drop: 16,
                key: "mark",
            },
        ],
        threshold: 0.5,
    },
    {
        className: styles.ursus,
        idleClassName: styles.ursusIdle,
        key: "ursus",
        parts: [
            {
                children: (
                    <>
                        <div className={styles.building} />
                        <div className={styles.flying} />
                        <PatrolSprite
                            spriteClassName={styles.insect}
                            wrapperClassName={styles.insectPatrol}
                        />
                    </>
                ),
                className: styles.block,
                delay: 0.02,
                drop: 34,
                key: "building",
            },
            {
                className: styles.mark,
                delay: 0.12,
                drop: 18,
                key: "mark",
            },
        ],
        threshold: 0.66,
    },
    {
        className: styles.kazdel,
        idleClassName: styles.kazdelIdle,
        key: "kazdel",
        parts: [
            {
                children: <div className={styles.building} />,
                className: styles.block,
                delay: 0.02,
                drop: 24,
                key: "building",
            },
            {
                children: (
                    <PatrolSprite
                        spriteClassName={styles.npc}
                        wrapperClassName={styles.npcPatrol}
                    />
                ),
                className: styles.block,
                delay: 0.12,
                drop: 24,
                key: "npc",
            },
            {
                children: (
                    <>
                        <div className={styles.stove} />
                        <PatrolSprite
                            spriteClassName={styles.insect}
                            wrapperClassName={styles.insectPatrol}
                        />
                    </>
                ),
                className: styles.block,
                delay: 0.22,
                drop: 40,
                key: "stove",
            },
            {
                className: styles.mark,
                delay: 0.32,
                drop: 18,
                key: "mark",
            },
        ],
        threshold: 0.73,
    },
    {
        className: styles.rhodes,
        idleClassName: styles.rhodesIdle,
        key: "rhodes",
        parts: [
            {
                children: (
                    <>
                        <div className={styles.icon} />
                        <div className={styles.medical} />
                    </>
                ),
                className: styles.block,
                delay: 0.02,
                drop: 32,
                key: "medical",
            },
            {
                className: styles.mark,
                delay: 0.12,
                drop: 18,
                key: "mark",
            },
        ],
        threshold: 1,
    },
] as const satisfies readonly RegionDefinition[]

interface BackgroundMapProps {
    width: number
    height: number
}

const BackgroundMap: React.FC<BackgroundMapProps> = ({ width, height }) => {
    const shouldReduceMotion = useReducedMotion() ?? false
    const globalView = useGlobalView()
    const globalStage = useGlobalStore((state) => state.globalStage)
    const loadingDisplayProgress = useGlobalStore(
        (state) => state.loadingDisplayProgress,
    )
    const backgroundMapIdleMotionEnabled = useGlobalStore(
        (state) => state.backgroundMapIdleMotionEnabled,
    )
    const [fontSize, setFontSize] = useState(getFontSize())
    const isProgressControlled =
        globalView === GlobalView.MAIN && globalStage === GlobalStage.LOADING
    const [unlockedRegions, setUnlockedRegions] = useState(() =>
        createRegionUnlocks(!isProgressControlled),
    )

    useEffect(() => {
        const id = window.setInterval(() => setFontSize(getFontSize()), 100)
        return () => window.clearInterval(id)
    }, [])

    useEffect(() => {
        if (!isProgressControlled) {
            setUnlockedRegions((current) => {
                if (Object.values(current).every(Boolean)) {
                    return current
                }

                return createRegionUnlocks(true)
            })
            return
        }

        if (loadingDisplayProgress <= 0) {
            setUnlockedRegions((current) => {
                if (Object.values(current).every((value) => !value)) {
                    return current
                }

                return createRegionUnlocks(false)
            })
            return
        }

        setUnlockedRegions((current) => {
            const next = { ...current }
            let changed = false

            for (const { key, threshold } of REGIONS) {
                if (loadingDisplayProgress >= threshold && !next[key]) {
                    next[key] = true
                    changed = true
                }
            }

            return changed ? next : current
        })
    }, [isProgressControlled, loadingDisplayProgress])

    const style = useMemo(() => {
        const fullWidth = width - (27 / 16) * fontSize * 2
        const currentWidth = (1869 / 16) * fontSize
        if (fullWidth > currentWidth) {
            return {
                transform: `translateX(-50%) scale(${fullWidth / currentWidth})`,
            }
        }

        const restHeight = height - 12.25 * fontSize
        const currentHeight = (672 / 16) * fontSize
        return {
            transformOrigin: "center top",
            transform: `translateX(-50%) scale(${Math.max(restHeight / currentHeight / 1.31548, 1)})`,
        }
    }, [height, fontSize, width])

    return (
        <div className={styles.map}>
            <div
                className={`${styles.content} ${
                    backgroundMapIdleMotionEnabled ? "" : styles.idlePaused
                }`}
                style={style}
            >
                {globalView === GlobalView.MAIN &&
                globalStage === GlobalStage.LOADING
                    ? REGIONS.map((region) => (
                          <Region
                              key={region.key}
                              definition={region}
                              active={unlockedRegions[region.key]}
                              animateOnEnter={isProgressControlled}
                              shouldReduceMotion={shouldReduceMotion}
                          />
                      ))
                    : null}
            </div>
        </div>
    )
}

export default BackgroundMap
